import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { ThunderboltFilled } from "@ant-design/icons";

import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import {
  BackgroundContainer,
  Card,
  ElementContainer,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { CompassFloatButton } from "../../components/buttons/CompassFloatButton";
import { AssessmentTriggerModalV2 } from "../../components/common/AIModals";
import { greyText } from "../../styles";
import { DashboardFilters } from "../../types/engagementAssessmentV2Types";

import {
  buildInitialDashboardFilters,
  DashboardFilterBar,
} from "./DashboardFilterBar";
import {
  applyDashboardFilters,
  computeManagerPillarHeatmap,
  computeManagerSinHeatmap,
  computeScatterPoints,
} from "./aggregations";
import { ManagerPillarHeatmap } from "./ManagerPillarHeatmap";
import { ManagerSinHeatmap } from "./ManagerSinHeatmap";
import { PillarsSinsScatter } from "./PillarsSinsScatter";
import { useAssessmentInsightsData } from "./useAssessmentInsightsData";

const PAGE_DESCRIPTION =
  "Cross-engagement quality signals from the latest v2 assessment runs. " +
  "Filter by manager, theme, and date range to narrow the view.";

const CardRowContainer = styled.div<{ pl?: string; pr?: string }>`
  display: flex;
  flex: 1;
  padding-left: ${({ pl }): string => pl || "0px"};
  padding-right: ${({ pr }): string => pr || "0px"};
`;

export const AssessmentInsights = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);

  const [filters, setFilters] = useState<DashboardFilters>(
    buildInitialDashboardFilters,
  );

  const { latestRuns, isLoading, error, isEmpty, refresh } =
    useAssessmentInsightsData(filters);

  const managerKey = filters.managerIds.slice().sort().join(",");
  const themeKey = filters.themes.slice().sort().join(",");
  const filteredLatestRuns = useMemo(
    () => applyDashboardFilters(latestRuns, filters),
    [
      latestRuns,
      managerKey,
      themeKey,
      filters.dateRange.start,
      filters.dateRange.end,
    ],
  );

  const managerPillarData = useMemo(
    () => computeManagerPillarHeatmap(filteredLatestRuns),
    [filteredLatestRuns],
  );
  const managerSinData = useMemo(
    () => computeManagerSinHeatmap(filteredLatestRuns),
    [filteredLatestRuns],
  );
  const scatterData = useMemo(
    () => computeScatterPoints(filteredLatestRuns),
    [filteredLatestRuns],
  );

  const [showBatchModal, setShowBatchModal] = useState<boolean>(false);
  const openBatchModal = useCallback((): void => setShowBatchModal(true), []);
  const closeBatchModal = useCallback((): void => setShowBatchModal(false), []);

  const handleBatchComplete = useCallback((): void => {
    refresh();
  }, [refresh]);

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={"assessment-insights"} />
          </RowContainer>

          <RowContainer ai="center" jc="space-between" mt={-15}>
            <ElementContainer ai="flex-start" flexDirection="column">
              <CompassMainHeader pt={1} ml={12}>
                Assessment Insights
              </CompassMainHeader>
            </ElementContainer>
          </RowContainer>

          <RowContainer ai="flex-start" jc="flex-start" mt={1}>
            <CompassParagraph ml={12} color={greyText}>
              {PAGE_DESCRIPTION}
            </CompassParagraph>
          </RowContainer>

          <RowContainer ai="center" jc="flex-start" ml={8} mb={8}>
            <DashboardFilterBar value={filters} onChange={setFilters} />
          </RowContainer>
        </NavBarContainer>

        <RowContainer ai="flex-start">
          <CardRowContainer pl="8px">
            <ManagerPillarHeatmap
              data={managerPillarData}
              isLoading={isLoading}
              error={isEmpty ? null : error}
              onRetry={refresh}
            />
          </CardRowContainer>
          <CardRowContainer pr="8px">
            <ManagerSinHeatmap
              data={managerSinData}
              isLoading={isLoading}
              error={isEmpty ? null : error}
              onRetry={refresh}
            />
          </CardRowContainer>
        </RowContainer>

        <RowContainer>
          <CardRowContainer pl="8px" pr="8px">
            <PillarsSinsScatter
              data={scatterData}
              isLoading={isLoading}
              error={isEmpty ? null : error}
              onRetry={refresh}
            />
          </CardRowContainer>
        </RowContainer>

        {!isLoading && isEmpty && (
          <RowContainer>
            <CardRowContainer pl="8px" pr="8px">
              <Card>
                <CompassParagraph fontSize={12} color={greyText}>
                  No v2 assessment runs match the current filters. Adjust the
                  filter bar or run a batch assessment to populate the
                  dashboard.
                </CompassParagraph>
              </Card>
            </CardRowContainer>
          </RowContainer>
        )}
      </Column>

      <CompassFloatButton
        variant="ai"
        shape="square"
        icon={<ThunderboltFilled />}
        label="Assess batch"
        tooltip="Run a batch v2 assessment"
        onClick={openBatchModal}
      />

      <AssessmentTriggerModalV2
        show={showBatchModal}
        onClose={closeBatchModal}
        mode="batch"
        onBatchComplete={handleBatchComplete}
      />
    </BackgroundContainer>
  );
};
